import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../config/axios";

const ACCEPT =
  ".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,application/pdf,image/*";

const PreConstructionUploadsModal = ({ open, onClose }) => {
  const [list, setList] = useState([]);
  const [nextNumber, setNextNumber] = useState(7000);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removingId, setRemovingId] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, nextRes] = await Promise.all([
        axiosInstance.get("/pre-construction-uploads"),
        axiosInstance.get("/pre-construction-uploads/next-number"),
      ]);
      setList(listRes.data || []);
      setNextNumber(nextRes.data?.nextNumber || 7000);
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Could not load pre-construction uploads.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadList();
    setSelected(null);
    setDragOver(false);
  }, [open, loadList]);

  const refreshSelected = async (id) => {
    if (!id) return;
    try {
      const res = await axiosInstance.get(`/pre-construction-uploads/${id}`);
      setSelected(res.data);
      setList((prev) =>
        prev.map((row) =>
          String(row._id) === String(id)
            ? {
                ...row,
                files: res.data.files || [],
                fileCount: (res.data.files || []).length,
              }
            : row,
        ),
      );
    } catch (_) {
      /* ignore */
    }
  };

  const handleCreate = async () => {
    try {
      setCreating(true);
      const res = await axiosInstance.post("/pre-construction-uploads", {});
      toast.success(`Created Pre Construction Upload #${res.data.uploadNumber}`);
      await loadList();
      setSelected(res.data);
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Could not create upload number.",
      );
    } finally {
      setCreating(false);
    }
  };

  const uploadFilesBatch = async (packId, files) => {
    const formData = new FormData();
    for (const file of files) formData.append("files", file);
    const res = await axiosInstance.post(
      `/pre-construction-uploads/${packId}/files`,
      formData,
      {
        timeout: 180000,
        headers: { "Content-Type": undefined },
      },
    );
    return res.data;
  };

  const handleUpload = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!selected?._id || !files.length) return;
    try {
      setUploading(true);
      let row;
      try {
        row = await uploadFilesBatch(selected._id, files);
      } catch (batchErr) {
        // Fall back to one-by-one so a large multi-select still lands
        if (files.length <= 1) throw batchErr;
        row = selected;
        let uploaded = 0;
        for (const file of files) {
          try {
            row = await uploadFilesBatch(selected._id, [file]);
            uploaded += 1;
          } catch (oneErr) {
            console.error("pre-con single file upload failed:", file.name, oneErr);
          }
        }
        if (!uploaded) throw batchErr;
        if (uploaded < files.length) {
          toast.warn(
            `Uploaded ${uploaded} of ${files.length} files. Some files could not be saved.`,
          );
        }
      }
      setSelected(row);
      if (files.length === 1 || (row.files || []).length) {
        toast.success(
          files.length === 1
            ? "Document uploaded. You can add more anytime."
            : `${files.length} documents uploaded. You can add more anytime.`,
        );
      }
      await loadList();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not upload documents.");
    } finally {
      setUploading(false);
      setDragOver(false);
    }
  };

  const handleRemove = async (fileId) => {
    if (!selected?._id || !fileId) return;
    try {
      setRemovingId(fileId);
      const res = await axiosInstance.delete(
        `/pre-construction-uploads/${selected._id}/files/${fileId}`,
      );
      setSelected(res.data);
      await loadList();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not remove file.");
    } finally {
      setRemovingId("");
    }
  };

  const openFile = async (fileId, originalName) => {
    if (!selected?._id || !fileId) return;
    try {
      const res = await axiosInstance.get(
        `/pre-construction-uploads/${selected._id}/files/${fileId}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          `Could not open ${originalName || "file"}.`,
      );
    }
  };

  const fileCount = selected?.files?.length ?? 0;

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pre-construction-uploads-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b">
          <div>
            <h3
              id="pre-construction-uploads-title"
              className="text-lg font-semibold text-gray-900"
            >
              Pre Construction Uploads
            </h3>
            <p className="text-sm text-gray-600 mt-0.5">
              Numbers start at 7000. Upload one or many documents under a number,
              then link that number when creating a pre-construction trade.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-2xl leading-none text-gray-500 hover:text-gray-800"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleCreate}
              disabled={creating}
              className="px-4 py-2 bg-blue-900 text-white rounded-md text-sm hover:bg-blue-800 disabled:bg-gray-400"
            >
              {creating
                ? "Creating…"
                : `Create Upload #${nextNumber}`}
            </button>
            <button
              type="button"
              onClick={loadList}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
            >
              Refresh
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-md overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-800">
                Upload numbers
              </div>
              {loading ? (
                <p className="p-3 text-sm text-gray-500">Loading…</p>
              ) : list.length === 0 ? (
                <p className="p-3 text-sm text-gray-500">
                  No uploads yet. Create one to start at #7000.
                </p>
              ) : (
                <ul className="max-h-72 overflow-y-auto divide-y">
                  {list.map((row) => (
                    <li key={row._id}>
                      <button
                        type="button"
                        onClick={() => refreshSelected(row._id)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${
                          selected && String(selected._id) === String(row._id)
                            ? "bg-blue-50 text-blue-900 font-medium"
                            : "text-gray-800"
                        }`}
                      >
                        #{row.uploadNumber}
                        <span className="ml-2 text-xs text-gray-500">
                          {(row.fileCount ?? row.files?.length ?? 0)} file(s)
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="border border-gray-200 rounded-md overflow-hidden flex flex-col">
              <div className="bg-gray-50 px-3 py-2 flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-gray-800">
                  {selected
                    ? `Documents for #${selected.uploadNumber}`
                    : "Select an upload number"}
                </span>
                {selected ? (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept={ACCEPT}
                      className="hidden"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        e.target.value = "";
                        if (files.length) handleUpload(files);
                      }}
                    />
                    <button
                      type="button"
                      disabled={uploading}
                      onClick={() => fileInputRef.current?.click()}
                      className="px-3 py-1 bg-blue-900 text-white rounded text-xs hover:bg-blue-800 disabled:bg-gray-400"
                      title="Select one or more files (Ctrl/Cmd+click or Shift+click)"
                    >
                      {uploading
                        ? "Uploading…"
                        : fileCount
                          ? "Add more files"
                          : "Upload files"}
                    </button>
                  </>
                ) : null}
              </div>
              {!selected ? (
                <p className="p-3 text-sm text-gray-500">
                  Choose a number on the left to view or add files.
                </p>
              ) : (
                <>
                  <div
                    className={`mx-3 mt-3 rounded-md border border-dashed px-3 py-4 text-center text-sm transition-colors ${
                      dragOver
                        ? "border-blue-600 bg-blue-50 text-blue-900"
                        : "border-gray-300 bg-gray-50 text-gray-600"
                    }`}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDragOver(true);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDragOver(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDragOver(false);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDragOver(false);
                      if (uploading) return;
                      const files = Array.from(e.dataTransfer?.files || []);
                      if (files.length) handleUpload(files);
                    }}
                  >
                    {uploading
                      ? "Uploading…"
                      : "Drop multiple files here, or use Add more files. PDF, images, or Word."}
                  </div>
                  {!fileCount ? (
                    <p className="p-3 text-sm text-gray-500">
                      No files yet. Upload one or many documents under this number.
                    </p>
                  ) : (
                    <ul className="max-h-56 overflow-y-auto divide-y mt-2">
                      {(selected.files || []).map((file) => (
                        <li
                          key={file.fileId}
                          className="flex items-center justify-between gap-2 px-3 py-2"
                        >
                          <button
                            type="button"
                            className="text-sm text-blue-900 hover:underline text-left break-all"
                            onClick={() =>
                              openFile(file.fileId, file.originalName)
                            }
                          >
                            {file.originalName || "File"}
                          </button>
                          <button
                            type="button"
                            disabled={removingId === file.fileId}
                            onClick={() => handleRemove(file.fileId)}
                            className="text-xs text-red-700 hover:underline shrink-0 disabled:text-gray-400"
                          >
                            {removingId === file.fileId ? "Removing…" : "Remove"}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md text-sm hover:bg-gray-200"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreConstructionUploadsModal;
